import React from "react";
import {
  CompareArrows,
  ContentCopy,
  CropLandscape,
  Fullscreen,
  FullscreenExit,
  Pause,
  PlayArrow,
  ViewColumn,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { SplitMode, SplitPageState } from "../../../utils/splitPageUtils";

import { SongContentToggles, SongContentTogglesProps } from "./SongContentToggles";

interface SongControlsDesktopProps extends SongContentTogglesProps {
  isScrolling: boolean;
  onToggleScroll: () => void;
  scrollSpeed: number[];
  onSpeedChange: (e: Event | null, newVal: number | number[]) => void;
  splitMode: SplitMode;
  onSplitModeChange: (
    event: React.MouseEvent<HTMLElement> | null,
    newMode: SplitMode | null
  ) => void;
  splitPageState: SplitPageState;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onIncreaseFont: () => void;
  onDecreaseFont: () => void;
  onCopyChords: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  showSplitIndicators?: boolean;
  onToggleSplitIndicators?: () => void;
}

const keycapSx = {
  fontFamily: "monospace",
  fontWeight: 700,
  fontSize: "0.72rem",
  lineHeight: 1,
  px: 0.6,
  py: 0.35,
  minWidth: 22,
  minHeight: 22,
  border: "1px solid #94a3b8",
  borderBottom: "3px solid #64748b",
  borderRadius: "4px",
  bgcolor: "#f1f5f9",
  color: "#334155",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  userSelect: "none" as const,
};

export const SongControlsDesktop: React.FC<SongControlsDesktopProps> = ({
  visibleContent,
  onVisibleContentChange,
  isScrolling,
  onToggleScroll,
  scrollSpeed,
  onSpeedChange,
  splitMode,
  onSplitModeChange,
  splitPageState,
  totalPages,
  onPrevPage,
  onNextPage,
  onIncreaseFont,
  onDecreaseFont,
  onCopyChords,
  isFullscreen,
  onToggleFullscreen,
  showSplitIndicators = true,
  onToggleSplitIndicators,
}) => {
  return (
    <Box
      role="toolbar"
      aria-label="Song controls"
      sx={{
        p: { xs: 0.75, sm: 1 },
        pb: 0.5,
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        flexWrap="nowrap"
        width="100%"
        sx={{ overflowX: "auto", "& > *": { flexShrink: 0 } }}
      >
        <Button
          size="small"
          variant={isScrolling ? "contained" : "outlined"}
          color={isScrolling ? "primary" : "inherit"}
          onClick={onToggleScroll}
          startIcon={isScrolling ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
          sx={{ textTransform: "none", fontSize: "0.85rem", borderRadius: 2 }}
        >
          {isScrolling ? (
            <Box display="flex" alignItems="center" gap={0.5}>
              Pause <Box component="span" sx={keycapSx}>w</Box>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={0.5}>
              Auto-Scroll <Box component="span" sx={keycapSx}>w</Box>
            </Box>
          )}
        </Button>

        {isScrolling && (
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 160 }}>
            <Box component="span" sx={keycapSx}>q</Box>
            <Slider
              value={scrollSpeed}
              onChange={onSpeedChange}
              min={0.01}
              max={1.0}
              step={0.01}
              size="small"
              sx={{ width: 100 }}
            />
            <Box component="span" sx={keycapSx}>e</Box>
            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ minWidth: 32 }}>
              {scrollSpeed[0].toFixed(2)}x
            </Typography>
          </Box>
        )}

        {/* Split View Toggle Controls */}
        <ToggleButtonGroup
          value={splitMode}
          exclusive
          size="small"
          onChange={onSplitModeChange}
          aria-label="split screen mode"
          sx={{ height: 30 }}
        >
          <Tooltip title="Single Page View (Key: 1)">
            <ToggleButton value={1} aria-label="1 page view" sx={{ px: 1 }}>
              <CropLandscape fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="2 Split View (Key: 2)">
            <ToggleButton value={2} aria-label="2 split view" sx={{ px: 1 }}>
              <ViewColumn fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="3 Split View (Key: 3)">
            <ToggleButton value={3} aria-label="3 split view" sx={{ px: 1 }}>
              <ViewColumn fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        {/* Split Indicators Toggle */}
        <Tooltip title="Toggle Split Indicators">
          <ToggleButton
            value="split-indicators"
            selected={Boolean(showSplitIndicators)}
            onClick={() => onToggleSplitIndicators?.()}
            size="small"
            aria-label="Toggle Split Indicators"
            data-testid="toggle-split-indicators-button"
            sx={{
              height: 30,
              px: 1,
              borderRadius: 1,
              borderColor: showSplitIndicators ? "primary.main" : "#e2e8f0",
              bgcolor: showSplitIndicators ? "#eff6ff" : "transparent",
            }}
          >
            <CompareArrows fontSize="small" />
          </ToggleButton>
        </Tooltip>

        <SongContentToggles visibleContent={visibleContent} onVisibleContentChange={onVisibleContentChange} />
        <Box display="flex" alignItems="center" gap={0.5} ml="auto">
          <IconButton onClick={onIncreaseFont} size="small" title="Increase Font (Key: n)">
            <Typography fontWeight="bold" fontSize="0.9rem">
              A+
            </Typography>
          </IconButton>
          <IconButton onClick={onDecreaseFont} size="small" title="Decrease Font (Key: m)">
            <Typography fontWeight="bold" fontSize="0.8rem">
              A-
            </Typography>
          </IconButton>
          <IconButton onClick={onCopyChords} size="small" title="Copy chords">
            <ContentCopy fontSize="small" />
          </IconButton>
          <IconButton onClick={onToggleFullscreen} size="small" title="Toggle Fullscreen (Key: f)">
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
